'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import ProgressIndicator from './ProgressIndicator';
import QuestionCard from './QuestionCard';
import RadioOption from './RadioOption';
import CheckboxOption from './CheckboxOption';
import InputField from './InputField';
import TextAreaField from './TextAreaField';
import ResultsCard from './ResultsCard';
import { trackEvent } from '@/lib/analytics';
import { clientService } from '@/services/clientService';

interface FormData {
  lifeStage: string;
  financialGoals: string[];
  currentSituation: string;
  monthlyIncome: string;
  existingPlanning: string;
  primaryConcern: string;
  timeframe: string;
  additionalInfo: string;
  name: string;
  email: string;
  phone: string;
  consent: boolean;
}

interface FormErrors {
  [key: string]: string;
}

interface ResultCardData {
  icon: string;
  title: string;
  description: string;
  color: 'primary' | 'secondary' | 'accent' | 'success';
}

export default function DiagnosisInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [hasBookingData, setHasBookingData] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    lifeStage: '',
    financialGoals: [],
    currentSituation: '',
    monthlyIncome: '',
    existingPlanning: '',
    primaryConcern: '',
    timeframe: '',
    additionalInfo: '',
    name: '',
    email: '',
    phone: '',
    consent: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const [diagnosisId, setDiagnosisId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [gaSessionId, setGaSessionId] = useState<string | null>(null);

  // Fix hydration - set to true after mount
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Load booking data from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('user_data');
        if (saved) {
          const data = JSON.parse(saved);
          if (data?.name && data?.email && data?.phone) {
            setFormData((prev) => ({
              ...prev,
              name: data.name,
              email: data.email,
              phone: data.phone,
              consent: true,
            }));
            setHasBookingData(true);
          }
        }
      } catch {}
    }
  }, []);

  // Initialize GA session tracking
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('get', 'G-XXXXXXXXXX', 'session_id', (sessionId: string) => {
        setGaSessionId(sessionId);
      });
    }
  }, []);

  // Track diagnosis start
  useEffect(() => {
    if (currentStep === 0) {
      trackEvent({
        action: 'diagnosis_start',
        category: 'engagement',
        label: 'Online Diagnosis Started',
      });
    }
  }, [currentStep]);

  // The visible steps depend on whether we have booking data
  // With booking data: 3 steps (0,1,2) — contact step is skipped
  // Without booking data: 4 steps (0,1,2,3)
  const stepLabels = hasBookingData
    ? ['Životní situace', 'Finanční cíle', 'Aktuální stav']
    : ['Životní situace', 'Finanční cíle', 'Aktuální stav', 'Kontaktní údaje'];

  const totalSteps = hasBookingData ? 3 : 4;
  // The last "real" step index before submission
  const lastStep = hasBookingData ? 2 : 3;

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    switch (step) {
      case 0:
        if (!formData.lifeStage) newErrors.lifeStage = 'Vyberte prosím vaši životní situaci';
        break;
      case 1:
        if (formData.financialGoals.length === 0) newErrors.financialGoals = 'Vyberte alespoň jeden finanční cíl';
        if (!formData.primaryConcern) newErrors.primaryConcern = 'Vyberte prosím vaši hlavní obavu';
        break;
      case 2:
        if (!formData.currentSituation) newErrors.currentSituation = 'Vyberte prosím váš aktuální stav';
        if (!formData.monthlyIncome) newErrors.monthlyIncome = 'Vyberte prosím váš měsíční příjem';
        if (!formData.existingPlanning) newErrors.existingPlanning = 'Vyberte prosím stav vašeho finančního plánování';
        break;
      case 3:
        if (!formData.name.trim()) newErrors.name = 'Zadejte prosím vaše jméno';
        if (!formData.email.trim()) newErrors.email = 'Zadejte prosím váš email';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Zadejte prosím platný email';
        if (!formData.phone.trim()) newErrors.phone = 'Zadejte prosím vaše telefonní číslo';
        if (!formData.consent) newErrors.consent = 'Musíte souhlasit se zpracováním osobních údajů';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      // Track step completion
      trackEvent({
        action: 'diagnosis_step',
        category: 'engagement',
        label: `Step ${currentStep} completed`,
      });
      
      if (currentStep < lastStep) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // ============================================================================
        // STEP 3 → 4: Create/Update Client + Complete Diagnosis
        // ============================================================================
        
        try {
          // 1. Upsert client by email (single source of truth)
          const clientResult = await clientService.upsertClientByEmail({
            email: formData.email,
            fullName: formData.name,
            phone: formData.phone,
            source: 'organic',
          });

          if (clientResult.error || !clientResult.data) {
            setErrors({ ...errors, consent: 'Nepodařilo se uložit údaje. Zkuste to prosím znovu.' });
            return;
          }

          const finalClientId = clientResult.data;
          setClientId(finalClientId);

          // 2. Prepare complete answers (all diagnosis steps + contact data)
          const completeAnswers = {
            // Contact data (from step 3)
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            consent: formData.consent,
            additionalInfo: formData.additionalInfo,
            
            // Diagnosis data (from steps 0-2)
            lifeStage: formData.lifeStage,
            financialGoals: formData.financialGoals,
            primaryConcern: formData.primaryConcern,
            currentSituation: formData.currentSituation,
            monthlyIncome: formData.monthlyIncome,
            existingPlanning: formData.existingPlanning,
            
            // GA tracking
            gaSessionId,
          };

          // 3. Create or update diagnosis result with complete answers
          let diagnosis;
          if (diagnosisId) {
            // Update existing diagnosis (if created earlier)
            const completionTime = Math.floor((Date.now() - startTime) / 1000);
            diagnosis = await clientService.completeDiagnosisResult(
              diagnosisId,
              completionTime,
              completeAnswers
            );
          } else {
            // Create new diagnosis with complete answers
            diagnosis = await clientService.createDiagnosisResult({
              clientId: finalClientId,
              answers: completeAnswers,
              gaSessionId: gaSessionId || undefined,
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
            });

            if (!diagnosis.error && diagnosis.data) {
              setDiagnosisId(diagnosis.data.id);
              
              // Now complete it immediately
              const completionTime = Math.floor((Date.now() - startTime) / 1000);
              await clientService.completeDiagnosisResult(
                diagnosis.data.id,
                completionTime,
                completeAnswers
              );
            }
          }

          if (diagnosis?.error) {
            setErrors({ ...errors, consent: 'Nepodařilo se dokončit diagnostiku. Zkuste to prosím znovu.' });
            return;
          }

          // 4. Track completion
          trackEvent({
            action: 'diagnosis_complete',
            category: 'conversion',
            label: 'Online Diagnosis Completed',
            value: Math.floor((Date.now() - startTime) / 1000),
          });

          // 5. Save user data to localStorage for booking page
          if (typeof window !== 'undefined') {
            const userData = {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
            };
            localStorage.setItem('user_data', JSON.stringify(userData));
          }

          // 6. Show results
          setCurrentStep(4);
          setShowResults(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          
        } catch (err) {
          setErrors({ ...errors, consent: 'Došlo k neočekávané chybě. Zkuste to prosím znovu.' });
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleGoalToggle = (value: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      financialGoals: checked ? [...prev.financialGoals, value] : prev.financialGoals.filter((g) => g !== value),
    }));
  };

  const generateResults = (): ResultCardData[] => {
    const results: ResultCardData[] = [];

    if (formData.lifeStage === 'buying-home') {
      results.push({
        icon: 'HomeIcon',
        title: 'Hypoteční poradenství',
        description:
          'Na základě vaší situace doporučujeme zaměřit se na optimalizaci hypotéky a zajištění nejlepších podmínek pro koupi nemovitosti v Praze.',
        color: 'primary',
      });
    }

    if (formData.financialGoals.includes('retirement')) {
      results.push({
        icon: 'ChartBarIcon',
        title: 'Důchodové plánování',
        description:
          'Vytvoříme dlouhodobý plán pro zajištění vašeho důchodu s ohledem na současnou situaci a budoucí potřeby.',
        color: 'secondary',
      });
    }

    if (formData.financialGoals.includes('investment')) {
      results.push({
        icon: 'CurrencyDollarIcon',
        title: 'Investiční strategie',
        description:
          'Navrhneme diverzifikovanou investiční strategii odpovídající vašemu rizikovému profilu a časovému horizontu.',
        color: 'accent',
      });
    }

    if (formData.existingPlanning === 'none') {
      results.push({
        icon: 'DocumentTextIcon',
        title: 'Základní finanční plán',
        description:
          'Začneme od základů - vytvoříme strukturovaný finanční plán, který vám přinese jasnost a kontrolu nad vašimi financemi.',
        color: 'success',
      });
    }

    if (results.length === 0) {
      results.push({
        icon: 'LightBulbIcon',
        title: 'Komplexní finanční analýza',
        description:
          'Na základě vašich odpovědí doporučujeme komplexní analýzu vaší finanční situace a vytvoření personalizovaného plánu.',
        color: 'primary',
      });
    }

    return results;
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-lg p-8 shadow-card">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="space-y-3 mt-8">
                  <div className="h-16 bg-muted rounded"></div>
                  <div className="h-16 bg-muted rounded"></div>
                  <div className="h-16 bg-muted rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const results = generateResults();

    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card border border-border rounded-lg p-6 lg:p-8 shadow-card mb-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
                  <Icon name="CheckCircleIcon" size={32} className="text-success" />
                </div>
                <h2 className="text-2xl lg:text-3xl font-semibold text-text-primary mb-2">Děkujeme za vyplnění diagnózy!</h2>
                <p className="text-text-secondary">Na základě vašich odpovědí jsme připravili personalizovaná doporučení</p>
              </div>

              <div className="space-y-4 mb-8">
                {results.map((result, index) => (
                  <ResultsCard key={index} data={result} />
                ))}
              </div>

              {/* SECONDARY CTA - Informational Escape Route */}
              <div className="text-center mb-6">
                <p className="text-sm text-text-secondary mb-3">
                  Nejste si jisti? Nejprve zjistěte, jak probíhá první schůzka
                </p>
                <Link
                  href="/first-meeting-analysis"
                  className="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-smooth"
                >
                  <Icon name="InformationCircleIcon" size={20} className="mr-2" />
                  Co očekávat na první schůzce
                </Link>
              </div>

              {/* Next Steps Information */}
              <div className="bg-trust border border-primary/20 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-text-primary mb-3">Co bude následovat?</h3>
                <ul className="space-y-3 text-sm text-text-secondary">
                  <li className="flex items-start">
                    <Icon name="CheckIcon" size={20} className="text-success mr-2 flex-shrink-0 mt-0.5" />
                    <span>Ozveme se vám do 24 hodin pro potvrzení schůzky</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="CheckIcon" size={20} className="text-success mr-2 flex-shrink-0 mt-0.5" />
                    <span>Připravíme předběžnou analýzu na základě vašich odpovědí</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="CheckIcon" size={20} className="text-success mr-2 flex-shrink-0 mt-0.5" />
                    <span>První schůzka bude trvat cca 60-90 minut a je zcela nezávazná</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="CheckIcon" size={20} className="text-success mr-2 flex-shrink-0 mt-0.5" />
                    <span>Společně vytvoříme základy vašeho finančního plánu</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => {
                  setShowResults(false);
                  setCurrentStep(0);
                  setFormData({
                    lifeStage: '',
                    financialGoals: [],
                    currentSituation: '',
                    monthlyIncome: '',
                    existingPlanning: '',
                    primaryConcern: '',
                    timeframe: '',
                    additionalInfo: '',
                    name: '',
                    email: '',
                    phone: '',
                    consent: false,
                  });
                  localStorage.removeItem('diagnosisFormData');
                  localStorage.removeItem('diagnosisCurrentStep');
                }}
                className="text-primary hover:text-primary/80 font-medium transition-smooth"
              >
                Vyplnit diagnózu znovu
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <span>Dobrovolná diagnóza</span>
            </div>
            <h1 className="text-3xl lg:text-4xl font-semibold text-text-primary mb-4">Získej lepší výsledek ze schůzky</h1>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Většina lidí přijde nepřipravená. A pak řešíme základní věci. Pokud chceš jít rovnou na podstatu — vyplň tohle. Připravíme se předem a schůzka bude konkrétní.
            </p>
            <p className="text-xs text-text-secondary mt-3">
              Diagnóza je zcela dobrovolná a nezávazná. Trvá přibližně 2 minuty.
            </p>
          </div>

          <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} stepLabels={stepLabels} />

          {currentStep === 0 && (
            <QuestionCard
              title="V jaké životní situaci se právě nacházíte?"
              description="Vyberte situaci, která nejlépe odpovídá vašemu aktuálnímu stavu"
              error={errors.lifeStage}
            >
              <RadioOption
                id="buying-home"
                name="lifeStage"
                value="buying-home"
                label="Plánuji koupi nemovitosti"
                description="Hledám hypotéku nebo řeším financování bydlení"
                checked={formData.lifeStage === 'buying-home'}
                onChange={(value) => setFormData({ ...formData, lifeStage: value })}
              />
              <RadioOption
                id="family-planning"
                name="lifeStage"
                value="family-planning"
                label="Plánuji rodinu"
                description="Chci zajistit finanční stabilitu pro budoucí rodinu"
                checked={formData.lifeStage === 'family-planning'}
                onChange={(value) => setFormData({ ...formData, lifeStage: value })}
              />
              <RadioOption
                id="career-change"
                name="lifeStage"
                value="career-change"
                label="Měním kariéru nebo podnikám"
                description="Potřebuji přehodnotit své finance kvůli změně v práci"
                checked={formData.lifeStage === 'career-change'}
                onChange={(value) => setFormData({ ...formData, lifeStage: value })}
              />
              <RadioOption
                id="mid-career"
                name="lifeStage"
                value="mid-career"
                label="Jsem ve stabilní kariéře"
                description="Chci optimalizovat své finance a investice"
                checked={formData.lifeStage === 'mid-career'}
                onChange={(value) => setFormData({ ...formData, lifeStage: value })}
              />
              <RadioOption
                id="retirement-planning"
                name="lifeStage"
                value="retirement-planning"
                label="Blíží se mi důchod"
                description="Potřebuji naplánovat finanční zajištění na důchod"
                checked={formData.lifeStage === 'retirement-planning'}
                onChange={(value) => setFormData({ ...formData, lifeStage: value })}
              />
            </QuestionCard>
          )}

          {currentStep === 1 && (
            <>
              <QuestionCard
                title="Jaké jsou vaše hlavní finanční cíle?"
                description="Můžete vybrat více možností"
                error={errors.financialGoals}
              >
                <CheckboxOption
                  id="goal-home"
                  name="financialGoals"
                  value="home"
                  label="Vlastní bydlení"
                  description="Koupě nebo refinancování nemovitosti"
                  checked={formData.financialGoals.includes('home')}
                  onChange={handleGoalToggle}
                />
                <CheckboxOption
                  id="goal-retirement"
                  name="financialGoals"
                  value="retirement"
                  label="Důchodové zajištění"
                  description="Příprava na finanční nezávislost v důchodu"
                  checked={formData.financialGoals.includes('retirement')}
                  onChange={handleGoalToggle}
                />
                <CheckboxOption
                  id="goal-investment"
                  name="financialGoals"
                  value="investment"
                  label="Investice a zhodnocení"
                  description="Růst majetku prostřednictvím investic"
                  checked={formData.financialGoals.includes('investment')}
                  onChange={handleGoalToggle}
                />
                <CheckboxOption
                  id="goal-protection"
                  name="financialGoals"
                  value="protection"
                  label="Ochrana rodiny"
                  description="Pojištění a finanční zabezpečení blízkých"
                  checked={formData.financialGoals.includes('protection')}
                  onChange={handleGoalToggle}
                />
                <CheckboxOption
                  id="goal-education"
                  name="financialGoals"
                  value="education"
                  label="Vzdělání dětí"
                  description="Spoření na budoucí vzdělání potomků"
                  checked={formData.financialGoals.includes('education')}
                  onChange={handleGoalToggle}
                />
              </QuestionCard>

              <div className="mt-6">
                <QuestionCard title="Co vás nejvíce trápí ohledně vašich financí?" error={errors.primaryConcern}>
                  <RadioOption
                    id="concern-clarity"
                    name="primaryConcern"
                    value="clarity"
                    label="Nemám jasno v tom, co mám dělat"
                    description="Potřebuji strukturu a směr"
                    checked={formData.primaryConcern === 'clarity'}
                    onChange={(value) => setFormData({ ...formData, primaryConcern: value })}
                  />
                  <RadioOption
                    id="concern-optimization"
                    name="primaryConcern"
                    value="optimization"
                    label="Nevím, jestli využívám své finance efektivně"
                    description="Chci optimalizovat své výdaje a investice"
                    checked={formData.primaryConcern === 'optimization'}
                    onChange={(value) => setFormData({ ...formData, primaryConcern: value })}
                  />
                  <RadioOption
                    id="concern-future"
                    name="primaryConcern"
                    value="future"
                    label="Obávám se o budoucnost"
                    description="Potřebuji jistotu a dlouhodobý plán"
                    checked={formData.primaryConcern === 'future'}
                    onChange={(value) => setFormData({ ...formData, primaryConcern: value })}
                  />
                  <RadioOption
                    id="concern-decisions"
                    name="primaryConcern"
                    value="decisions"
                    label="Bojím se udělat špatné rozhodnutí"
                    description="Chci odborné vedení při důležitých krocích"
                    checked={formData.primaryConcern === 'decisions'}
                    onChange={(value) => setFormData({ ...formData, primaryConcern: value })}
                  />
                </QuestionCard>
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <QuestionCard title="Jak byste popsali svou aktuální finanční situaci?" error={errors.currentSituation}>
                <RadioOption
                  id="situation-stable"
                  name="currentSituation"
                  value="stable"
                  label="Stabilní příjem, pravidelné spoření"
                  description="Mám přehled a kontrolu nad financemi"
                  checked={formData.currentSituation === 'stable'}
                  onChange={(value) => setFormData({ ...formData, currentSituation: value })}
                />
                <RadioOption
                  id="situation-variable"
                  name="currentSituation"
                  value="variable"
                  label="Proměnlivý příjem"
                  description="Příjem se měsíc od měsíce liší"
                  checked={formData.currentSituation === 'variable'}
                  onChange={(value) => setFormData({ ...formData, currentSituation: value })}
                />
                <RadioOption
                  id="situation-chaotic"
                  name="currentSituation"
                  value="chaotic"
                  label="Chaotická, bez přehledu"
                  description="Nevím přesně, kam peníze odcházejí"
                  checked={formData.currentSituation === 'chaotic'}
                  onChange={(value) => setFormData({ ...formData, currentSituation: value })}
                />
                <RadioOption
                  id="situation-transition"
                  name="currentSituation"
                  value="transition"
                  label="V přechodném období"
                  description="Právě procházím změnou (práce, rodina, atd.)"
                  checked={formData.currentSituation === 'transition'}
                  onChange={(value) => setFormData({ ...formData, currentSituation: value })}
                />
              </QuestionCard>

              <div className="mt-6">
                <QuestionCard title="Jaký je váš přibližný měsíční čistý příjem?" error={errors.monthlyIncome}>
                  <RadioOption
                    id="income-30"
                    name="monthlyIncome"
                    value="30"
                    label="Do 30 000 Kč"
                    checked={formData.monthlyIncome === '30'}
                    onChange={(value) => setFormData({ ...formData, monthlyIncome: value })}
                  />
                  <RadioOption
                    id="income-50"
                    name="monthlyIncome"
                    value="50"
                    label="30 000 - 50 000 Kč"
                    checked={formData.monthlyIncome === '50'}
                    onChange={(value) => setFormData({ ...formData, monthlyIncome: value })}
                  />
                  <RadioOption
                    id="income-80"
                    name="monthlyIncome"
                    value="80"
                    label="50 000 - 80 000 Kč"
                    checked={formData.monthlyIncome === '80'}
                    onChange={(value) => setFormData({ ...formData, monthlyIncome: value })}
                  />
                  <RadioOption
                    id="income-100"
                    name="monthlyIncome"
                    value="100"
                    label="80 000 - 100 000 Kč"
                    checked={formData.monthlyIncome === '100'}
                    onChange={(value) => setFormData({ ...formData, monthlyIncome: value })}
                  />
                  <RadioOption
                    id="income-more"
                    name="monthlyIncome"
                    value="more"
                    label="Více než 100 000 Kč"
                    checked={formData.monthlyIncome === 'more'}
                    onChange={(value) => setFormData({ ...formData, monthlyIncome: value })}
                  />
                </QuestionCard>
              </div>

              <div className="mt-6">
                <QuestionCard title="Máte již nějaké finanční plánování?" error={errors.existingPlanning}>
                  <RadioOption
                    id="planning-none"
                    name="existingPlanning"
                    value="none"
                    label="Ne, začínám od nuly"
                    checked={formData.existingPlanning === 'none'}
                    onChange={(value) => setFormData({ ...formData, existingPlanning: value })}
                  />
                  <RadioOption
                    id="planning-basic"
                    name="existingPlanning"
                    value="basic"
                    label="Mám základní přehled"
                    description="Sleduji příjmy a výdaje"
                    checked={formData.existingPlanning === 'basic'}
                    onChange={(value) => setFormData({ ...formData, existingPlanning: value })}
                  />
                  <RadioOption
                    id="planning-structured"
                    name="existingPlanning"
                    value="structured"
                    label="Mám strukturovaný plán"
                    description="Ale chci ho zlepšit nebo aktualizovat"
                    checked={formData.existingPlanning === 'structured'}
                    onChange={(value) => setFormData({ ...formData, existingPlanning: value })}
                  />
                  <RadioOption
                    id="planning-advisor"
                    name="existingPlanning"
                    value="advisor"
                    label="Pracuji s jiným poradcem"
                    description="Ale nejsem spokojený/á"
                    checked={formData.existingPlanning === 'advisor'}
                    onChange={(value) => setFormData({ ...formData, existingPlanning: value })}
                  />
                </QuestionCard>
              </div>
            </>
          )}

          {currentStep === 3 && !hasBookingData && (
            <>
              <QuestionCard title="Kontaktní údaje" description="Tyto informace použijeme pouze pro kontaktování ohledně vaší diagnózy">
                <InputField
                  id="name"
                  name="name"
                  label="Celé jméno"
                  placeholder="Jan Novák"
                  value={formData.name}
                  onChange={(value) => setFormData({ ...formData, name: value })}
                  error={errors.name}
                  required
                />
                <InputField
                  id="email"
                  name="email"
                  type="email"
                  label="Email"
                  placeholder="jan.novak@example.com"
                  value={formData.email}
                  onChange={(value) => setFormData({ ...formData, email: value })}
                  error={errors.email}
                  required
                />
                <InputField
                  id="phone"
                  name="phone"
                  type="tel"
                  label="Telefonní číslo"
                  placeholder="+420 123 456 789"
                  value={formData.phone}
                  onChange={(value) => setFormData({ ...formData, phone: value })}
                  error={errors.phone}
                  required
                />
                <TextAreaField
                  id="additionalInfo"
                  name="additionalInfo"
                  label="Další informace (volitelné)"
                  placeholder="Zde můžete uvést jakékoli další informace, které považujete za důležité..."
                  value={formData.additionalInfo}
                  onChange={(value) => setFormData({ ...formData, additionalInfo: value })}
                  rows={4}
                />
              </QuestionCard>

              <div className="mt-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.consent}
                      onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                      className="mt-1 w-4 h-4 text-primary focus:ring-primary focus:ring-2 rounded"
                    />
                    <span className="ml-3 text-sm text-text-secondary">
                      Souhlasím se zpracováním osobních údajů pro účely kontaktování a poskytování finančního poradenství v souladu s{' '}
                      <Link href="/privacy-policy" className="text-primary hover:underline">
                        Zásadami ochrany osobních údajů
                      </Link>
                      . Tento souhlas mohu kdykoli odvolat.
                    </span>
                  </label>
                  {errors.consent && <p className="text-xs text-error mt-2 ml-7">{errors.consent}</p>}
                </div>
              </div>
            </>
          )}

          <div className="flex items-center justify-between mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className={`px-6 py-3 font-semibold rounded-md transition-smooth flex items-center space-x-2 ${
                currentStep === 0
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-surface text-text-primary hover:bg-border'
              }`}
            >
              <Icon name="ChevronLeftIcon" size={20} className="text-current" />
              <span>Zpět</span>
            </button>

            <button
              onClick={handleNext}
              className="px-6 py-3 bg-cta text-cta-foreground font-semibold rounded-md hover:bg-cta-hover hover:shadow-hover transition-smooth flex items-center space-x-2"
            >
              <span>{currentStep === lastStep ? 'Odeslat diagnózu' : 'Pokračovat'}</span>
              <Icon name="ChevronRightIcon" size={20} className="text-current" />
            </button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-text-secondary">
              Máte otázky?{' '}
              <Link href="/how-we-work" className="text-primary hover:underline font-medium">
                Podívejte se, jak pracujeme
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}